import React, { useEffect, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import { useUserRolePermissions } from 'hooks';
import * as R from 'ramda';

import { NoAuthorizationsAvailable } from 'feature/loading-management/shared-components';
import { EmptyCard } from 'common';
import Header from './Header';
import CustomerAuthCard from './CustomerAuthCardConnector';
import ActionBar from './ActionBarConnector';
import SelectedAuthorizations from './SelectedAuthorizations';

const scrollbarStyles = {
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  height: 'calc(100% + 132px)',
  overflowY: 'scroll',
  pb: 4,
  scrollbarWidth: 'none',
};

const MainPanel = ({
  customerAuthorizations,
  fetchCustomerAuthorizations,
  fetchingCustomerAuthorizations,
  refetchToggle,
  page,
  results,
  selectedAuthorizations,
  setCustomerAuthorizationId,
  setSelectedAuthorizations,
  setPageNumber,
  setResultsPerPage,
  totalResults,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { currentAffiliate, isProductLoadingManage, isSupplier } = useUserRolePermissions();

  const isSupplierAndManager = isSupplier && isProductLoadingManage;

  const [showSelectedAuthorizations, setShowSelectedAuthorizations] = useState(false);

  const renderEmpty = R.cond([
    [
      R.always(fetchingCustomerAuthorizations),
      R.always(
        <Box
          sx={{
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            height: { xs: 'calc(100vh - 150px)', lg: 'calc(100vh - 97px)' },
            position: 'relative',
            width: 1,
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              left: { xs: 'calc(50% - 80px)', lg: 'calc(50% - 48px)' },
              position: 'absolute',
              top: 'calc(50% - 48px)',
            }}
          >
            <CircularProgress size={48} />
            <Typography sx={{ color: 'text.primary', ml: 1 }} variant="h5">
              Loading...
            </Typography>
          </Box>
        </Box>
      ),
    ],
    [
      R.always(R.isNil(customerAuthorizations)),
      R.always(
        <EmptyCard
          subtitle="Search and filter to narrow your results"
          title="Which authorizations are you searching for?"
        />
      ),
    ],
    [
      R.always(R.isEmpty(customerAuthorizations)),
      R.always(
        <Box sx={{ display: 'flex', justifyContent: 'center', width: 1 }}>
          <NoAuthorizationsAvailable />
        </Box>
      ),
    ],
  ]);

  const renderAuthorizations = R.ifElse(
    R.always(
      R.and(R.not(R.isNil(customerAuthorizations)), R.not(R.isEmpty(customerAuthorizations)))
    ),
    R.always(
      <>
        {isSupplierAndManager && (
          <ActionBar
            setShowSelectedAuthorizations={setShowSelectedAuthorizations}
            showSelectedAuthorizations={showSelectedAuthorizations}
          />
        )}
        {customerAuthorizations?.map(customerAuthorization => (
          <CustomerAuthCard
            customerAuthorization={customerAuthorization}
            key={customerAuthorization.customerId}
            setCustomerAuthorizationId={setCustomerAuthorizationId}
          />
        ))}
      </>
    ),
    R.always(renderEmpty())
  );

  const handleClickUnselectAll = () => {
    setSelectedAuthorizations([]);
    setShowSelectedAuthorizations(false);
  };

  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));

  useEffect(() => {
    if (currentAffiliate) {
      fetchCustomerAuthorizations(currentAffiliate);
    }
  }, [refetchToggle]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ marginLeft: { lg: '360px' }, marginTop: { lg: '49px' } }}>
      <Box
        sx={{
          padding: theme => ({
            /* eslint-disable sort-keys-fix/sort-keys-fix */
            xs: theme.spacing(14, 2, '52px'),
            lg: theme.spacing(0, 2, '52px'),
            /* eslint-enable sort-keys-fix/sort-keys-fix */
          }),
        }}
      >
        <Header />
        {isMobile && (
          <Drawer
            anchor="right"
            onClose={() => setShowSelectedAuthorizations(false)}
            open={showSelectedAuthorizations}
            slotProps={{
              paper: {
                sx: { width: '100%' },
              },
            }}
            variant="temporary"
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Box
                alignItems="center"
                bgcolor="surface.white"
                display="flex"
                height="64px"
                justifyContent="flex-end"
                paddingLeft={2.5}
                paddingTop={1}
                zIndex={1}
              >
                <IconButton
                  onClick={() => setShowSelectedAuthorizations(false)}
                  size="large"
                  sx={{
                    color: 'secondary.medium',
                    marginRight: 2,
                  }}
                >
                  <CloseRoundedIcon />
                </IconButton>
              </Box>
              <Box sx={scrollbarStyles}>
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mx: 2 }}
                >
                  <Typography variant="h6">Selected Auths</Typography>
                  <Button
                    color="primary"
                    onClick={handleClickUnselectAll}
                    sx={{ p: 0 }}
                    variant="text"
                  >
                    Unselect All
                  </Button>
                </Stack>
                <SelectedAuthorizations
                  selectedAuthorizations={selectedAuthorizations}
                  setSelectedAuthorizations={setSelectedAuthorizations}
                  setShowSelectedAuthorizations={setShowSelectedAuthorizations}
                />
              </Box>
            </Box>
          </Drawer>
        )}
        {renderAuthorizations()}
      </Box>
      {!!customerAuthorizations?.length && !fetchingCustomerAuthorizations && (
        <TablePagination
          component="div"
          count={totalResults}
          labelRowsPerPage={lgUp ? 'Rows per page' : ''}
          onPageChange={(_, newPage) => {
            // eslint-disable-next-line no-param-reassign
            setPageNumber(newPage);
            fetchCustomerAuthorizations(currentAffiliate);
            window.scrollTo(0, 0);
          }}
          onRowsPerPageChange={event => {
            setResultsPerPage(event.target.value);
            setPageNumber(0);
            fetchCustomerAuthorizations(currentAffiliate);
            window.scrollTo(0, 0);
          }}
          page={page}
          rowsPerPage={results}
          rowsPerPageOptions={[25, 50, 75, 100]}
          showFirstButton
          showLastButton
          sx={{
            '.MuiInputBase-root': {
              margin: theme => ({
                /* eslint-disable sort-keys-fix/sort-keys-fix */
                xs: 0,
                lg: theme.spacing(0, 4, 0, 2),
                /* eslint-enable sort-keys-fix/sort-keys-fix */
              }),
            },
            '.MuiTablePagination-selectLabel': {
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              display: { xs: 'none', lg: 'unset' },
            },
            '.MuiTablePagination-spacer': {
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              display: { xs: 'none', lg: 'unset' },
            },
            '.MuiTablePagination-toolbar': {
              '.MuiTablePagination-actions': {
                '& .MuiButtonBase-root:not([disabled])': {
                  color: 'primary.main',
                },
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                marginLeft: { xs: 0, lg: 2 },
              },
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              justifyContent: { xs: 'space-between', lg: 'unset' },
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              margin: { xs: '0 auto', lg: 'unset' },
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              maxWidth: { xs: 500, lg: 'unset' },
              padding: theme => theme.spacing(0, 2),
            },
            backgroundColor: 'surface.light',
            bottom: 0,
            justifyContent: 'space-between',
            position: 'fixed',
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            px: { xs: 2, lg: 'unset' },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            py: { xs: 0, lg: 'unset' },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            width: { xs: '100%', lg: 'calc(100% - 360px)' },
          }}
        />
      )}
    </Box>
  );
};

MainPanel.propTypes = {
  customerAuthorizations: arrayOf(shape({})),
  fetchCustomerAuthorizations: func.isRequired,
  fetchingCustomerAuthorizations: bool.isRequired,
  page: number.isRequired,
  refetchToggle: bool.isRequired,
  results: number.isRequired,
  selectedAuthorizations: arrayOf(shape({})).isRequired,
  setCustomerAuthorizationId: func.isRequired,
  setPageNumber: func.isRequired,
  setResultsPerPage: func.isRequired,
  setSelectedAuthorizations: func.isRequired,
  totalResults: number.isRequired,
};

MainPanel.defaultProps = {
  customerAuthorizations: null,
};

export default MainPanel;
